import { Target, CheckCircle, Clock, Users, AlertTriangle, Plus, Lock } from 'lucide-react'
import { Card, Badge } from '../../components/ui'
import ObiettivoCard from '../../components/pep/ObiettivoCard'
import NoteRapide from '../../components/pep/NoteRapide'
import ColloquioCard from '../../components/pep/ColloquioCard'

/**
 * PepTab — Tab "Progetto Educativo"
 *
 * Mostra: barra stato PEP, KPI, lista obiettivi con barre progresso,
 * ultimi colloqui. Empty state se nessun PEP attivo.
 *
 * Props:
 *   pep              - PEP attivo (null se assente)
 *   isCoord          - Boolean, true se coordinatore+
 *   isEducatore      - Boolean, ruolo educatore
 *   onCreatePep      - callback per avviare wizard creazione
 *   onSelectObiettivo - callback click su obiettivo
 *   onAddVerifica    - callback(obId) — apre form verifica
 *   onEditObiettivo  - callback(obId) — apre form modifica obiettivo
 *   onAddNota        - callback(obId, testo) — aggiunge nota rapida
 *   onReload         - callback() — ricarica dati PEP
 */

const FASE_LABELS = {
  accoglienza: 'Accoglienza',
  osservazione: 'Osservazione',
  progetto_attivo: 'Progetto attivo',
  verifica: 'Verifica',
  chiusura: 'Chiusura'
}

const FASE_COLORS = {
  accoglienza: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  osservazione: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  progetto_attivo: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  verifica: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  chiusura: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
}

export default function PepTab({ pep, isCoord, isEducatore, onCreatePep, onSelectObiettivo, onAddVerifica, onEditObiettivo, onAddNota, onAddColloquio, onReload, onImportRapido, onChiudiPep }) {
  if (!pep) {
    return <EmptyPep isCoord={isCoord} onCreatePep={onCreatePep} onImportRapido={onImportRapido} />
  }

  const obiettivi = pep.obiettivi || []
  const obiettiviAttivi = obiettivi.filter(o => o.stato === 'attivo')
  const obiettiviRaggiunti = obiettivi.filter(o => o.stato === 'raggiunto')
  // Educatore vede solo colloqui con visibilita='tutti' (difesa in profondità — il backend già filtra)
  const allColloqui = pep.colloqui || []
  const colloqui = isCoord ? allColloqui : allColloqui.filter(c => c.visibilita !== 'solo_coordinatore')
  const oggi = new Date()

  // Prossime verifiche in scadenza
  const verificheInScadenza = obiettiviAttivi
    .filter(o => o.dataScadenzaVerifica)
    .map(o => ({
      ...o,
      giorniRimanenti: Math.ceil(
        (new Date(o.dataScadenzaVerifica) - oggi) / (1000 * 60 * 60 * 24)
      )
    }))
    .sort((a, b) => a.giorniRimanenti - b.giorniRimanenti)

  const verificheScadute = verificheInScadenza.filter(v => v.giorniRimanenti < 0)

  return (
    <div className="space-y-5">
      {/* Barra stato PEP */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${FASE_COLORS[pep.faseCorrente] || FASE_COLORS.accoglienza}`}>
          {FASE_LABELS[pep.faseCorrente] || pep.faseCorrente}
        </span>
        {pep.stato === 'sospeso' && (
          <Badge variant="warning">Sospeso</Badge>
        )}
        {pep.stato === 'chiuso' && (
          <Badge variant="default">Chiuso</Badge>
        )}
        <span className="text-xs sm:text-sm text-gray-500">
          Avviato il {new Date(pep.dataAvvio).toLocaleDateString('it-IT')}
          {pep.durataMesi && ` — ${pep.durataMesi} mesi`}
        </span>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={<Target className="h-5 w-5 text-blue-500" />}
          value={obiettiviAttivi.length}
          label="Obiettivi attivi"
        />
        <KpiCard
          icon={<CheckCircle className="h-5 w-5 text-green-500" />}
          value={obiettiviRaggiunti.length}
          label="Raggiunti"
        />
        <KpiCard
          icon={<Users className="h-5 w-5 text-purple-500" />}
          value={colloqui.length}
          label="Colloqui"
        />
        <KpiCard
          icon={verificheScadute.length > 0
            ? <AlertTriangle className="h-5 w-5 text-red-500" />
            : <Clock className="h-5 w-5 text-orange-500" />
          }
          value={verificheInScadenza.length}
          label="Verifiche pendenti"
          alert={verificheScadute.length > 0}
        />
      </div>

      {/* Progresso complessivo */}
      {obiettivi.length > 0 && (
        <Card className="!p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Progresso complessivo
            </span>
            <span className="text-sm font-bold text-primary-600">
              {pep.progressoComplessivo || 0}%
            </span>
          </div>
          <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-green-500 rounded-full transition-all duration-500"
              style={{ width: `${pep.progressoComplessivo || 0}%` }}
            />
          </div>
        </Card>
      )}

      {/* Obiettivi */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Obiettivi ({obiettivi.length})
          </h3>
          {isCoord && pep.stato === 'attivo' && (
            <button
              onClick={() => onSelectObiettivo?.('new')}
              className="btn-secondary text-sm flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" /> Aggiungi
            </button>
          )}
        </div>

        {obiettivi.length === 0 ? (
          <p className="text-center text-gray-500 py-6 text-sm">
            Nessun obiettivo definito
          </p>
        ) : (
          <div className="space-y-3">
            {obiettivi.map(ob => (
              <div key={ob._id}>
                <ObiettivoCard
                  obiettivo={ob}
                  pepId={pep._id}
                  isCoord={isCoord}
                  isEducatore={isEducatore}
                  onAddVerifica={onAddVerifica}
                  onEdit={onEditObiettivo}
                  onReload={onReload}
                />
                {/* Note rapide — solo coordinatore, sotto la card */}
                {isCoord && (
                  <div className="ml-1 px-4 pb-3">
                    <NoteRapide
                      noteInterne={ob.noteInterne || []}
                      pepId={pep._id}
                      obiettivoId={ob._id}
                      onAddNota={(testo) => onAddNota?.(ob._id, testo)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Colloqui */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Colloqui ({colloqui.length})
          </h3>
          {isCoord && pep.stato === 'attivo' && (
            <button
              onClick={() => onAddColloquio?.()}
              className="btn-secondary text-sm flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" /> Nuovo colloquio
            </button>
          )}
        </div>

        {colloqui.length === 0 ? (
          <p className="text-center text-gray-500 py-4 text-sm">
            Nessun colloquio registrato
          </p>
        ) : (
          <div className="space-y-2">
            {[...colloqui]
              .sort((a, b) => new Date(b.dataEvento) - new Date(a.dataEvento))
              .map(coll => (
                <ColloquioCard
                  key={coll._id}
                  colloquio={coll}
                  isCoord={isCoord}
                />
              ))}
          </div>
        )}
      </div>

      {/* Chiudi PEP — solo coordinatore, solo PEP attivo */}
      {isCoord && pep.stato === 'attivo' && onChiudiPep && (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onChiudiPep}
            className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
          >
            <Lock className="h-4 w-4" />
            Chiudi progetto educativo
          </button>
        </div>
      )}
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────

function KpiCard({ icon, value, label, alert }) {
  return (
    <Card className={`!p-2.5 sm:!p-3 text-center ${alert ? 'ring-2 ring-red-300 dark:ring-red-700' : ''}`}>
      <div className="flex justify-center mb-0.5 sm:mb-1">{icon}</div>
      <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">{label}</p>
    </Card>
  )
}

function EmptyPep({ isCoord, onCreatePep, onImportRapido }) {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 mx-auto mb-4 bg-primary-50 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
        <Target className="h-8 w-8 text-primary-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        Nessun progetto educativo
      </h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto text-sm">
        Non è ancora stato avviato un progetto educativo personalizzato per questo minore.
      </p>
      {isCoord && (
        <div className="flex flex-col items-center gap-3">
          <button onClick={onCreatePep} className="btn-primary">
            <Plus className="h-4 w-4 mr-2 inline" />
            Avvia Progetto Educativo
          </button>
          {onImportRapido && (
            <button onClick={onImportRapido} className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
              Crea PEP per più minori in una volta
            </button>
          )}
        </div>
      )}
    </div>
  )
}
